import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useRef, useState } from "react";

import { useSession } from "../auth-client";
import { completeEmpty } from "../onboarding/api";
import { InterviewSurface } from "../onboarding/InterviewSurface";
import {
  ONBOARDING_STATE_KEY,
  useOnboardingState,
} from "../onboarding/use-onboarding-state";
import { Redirect } from "./-redirect";

export const Route = createFileRoute("/onboarding")({
  component: OnboardingPage,
});

/**
 * Onboarding opens straight on the interviewer — no chooser. The way out is a
 * small "Skip this, I'll set it up myself" link underneath, which runs the
 * empty path (seed the baseline workspace, mark onboarded). Both paths converge
 * on `onboarded = true`, after which the workspace gate lets the user in.
 */
function OnboardingPage() {
  const { data: session, isPending } = useSession();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Latched (a ref, so the very next render sees it) while `finish` owns the
  // exit. Without it the guard below RACES the summary-carrying navigation:
  // `finish` first refetches onboarded=true, that re-render mounts the
  // guard's `<Redirect to="/" />` (no search params!) during the still-pending
  // `navigate({search: {summary}})`, and the router cancels the summary
  // navigation for the guard's bare one — the hand-off card never showed.
  const finishingRef = useRef(false);

  const state = useOnboardingState();

  if (isPending || state.isLoading) {
    return (
      <div className="flex h-dvh items-center justify-center text-muted-foreground">
        Loading…
      </div>
    );
  }
  if (!session) return <Redirect to="/sign-in" />;
  // The guard's real job is bouncing an ALREADY-onboarded visitor who lands
  // here; the finish path exits on its own terms.
  if (state.data?.onboarded && !finishingRef.current) {
    return <Redirect to="/" />;
  }

  // `summary` (interview path only) rides to the home page in the URL, which
  // reads it, shows the hand-off card, and strips it. The skip path has no
  // summary. Invalidate first so the workspace gate refetches onboarded=true
  // (navigating on a stale-false cache would bounce straight back here).
  const finish = async (summary?: string) => {
    finishingRef.current = true;
    await qc.invalidateQueries({ queryKey: ONBOARDING_STATE_KEY });
    navigate({ to: "/", search: summary ? { summary } : {} });
  };

  const handleSkip = async () => {
    setBusy(true);
    setError(null);
    try {
      await completeEmpty();
      await finish();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to start workspace",
      );
      setBusy(false);
    }
  };

  return (
    <div className="flex h-dvh flex-col">
      <header className="flex h-14 shrink-0 items-center border-b border-border px-6">
        <div className="flex items-baseline gap-3">
          <h1 className="font-serif text-xl font-semibold tracking-tight">
            Hearth
          </h1>
          <span className="text-sm text-muted-foreground">
            Setting up your workspace
          </span>
        </div>
      </header>
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <InterviewSurface onComplete={(summary) => void finish(summary)} />
      </div>
      <footer className="shrink-0 border-t border-border py-3 text-center">
        <button
          type="button"
          onClick={handleSkip}
          disabled={busy}
          className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground disabled:opacity-50"
        >
          {busy ? "Setting up…" : "Skip this, I'll set it up myself"}
        </button>
        {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
      </footer>
    </div>
  );
}
